/**
 * The scope + period every Dashboard sub-page reads off its URL (spec 179 §5.5;
 * decisions.md I-1): `?propertyId=<uuid>` · `?myPortfolio=true` ·
 * `?from=YYYY-MM-DD&to=YYYY-MM-DD` — the range picker's own calendar days on
 * the browser's clock. Absent = the Dashboard's default range, [now − 30 d, now].
 *
 * The scope a person set on the Dashboard is the scope of the sub-page and of
 * its row links — it is read here, once, and never re-derived.
 */
import dayjs, { type Dayjs } from 'dayjs';
import { defaultRange, readScopeFromUrl } from '../../components/dashboard/dashboardModel';

export interface SubPageScope {
  propertyId?: string;
  myPortfolio: boolean;
  /** The picker's calendar days (YYYY-MM-DD) as the URL carried them; absent when it did not. */
  from?: string;
  to?: string;
  range: [Dayjs, Dayjs];
  /** What the analytics endpoints read — the same object DashboardPage sends. */
  queryParams: { startDate: string; endDate: string; propertyId?: string; myPortfolio?: boolean };
  /** The breadcrumb's first crumb: the Dashboard with the same four params. */
  dashboardHref: string;
}

/**
 * ONE reader of the I-1 params (`readScopeFromUrl`, the Dashboard's own) and
 * ONE default window (`defaultRange`) — a sub-page can never disagree with the
 * page that opened it about what the URL means (audit p3-11).
 */
export function readSubPageScope(search: string, now: Dayjs = dayjs()): SubPageScope {
  const url = readScopeFromUrl(search);
  const propertyId = url.propertyId;
  const myPortfolio = !propertyId && url.myPortfolio;
  const fallback = defaultRange(now);
  const range: [Dayjs, Dayjs] = [
    url.from ? dayjs(url.from).startOf('day') : fallback[0],
    url.to ? dayjs(url.to).endOf('day') : fallback[1],
  ];

  const back = new URLSearchParams();
  if (propertyId) back.set('propertyId', propertyId);
  if (myPortfolio) back.set('myPortfolio', 'true');
  if (url.from) back.set('from', url.from);
  if (url.to) back.set('to', url.to);
  const qs = back.toString();

  return {
    propertyId,
    myPortfolio,
    from: url.from,
    to: url.to,
    range,
    queryParams: {
      startDate: range[0].toISOString(),
      endDate: range[1].toISOString(),
      propertyId,
      myPortfolio: myPortfolio || undefined,
    },
    dashboardHref: `/dashboard${qs ? `?${qs}` : ''}`,
  };
}

/** The old page's tabs → the sub-page that took each one over (spec 179 §5.5). */
const TAB_TO_PATH: Record<string, string> = {
  'ai-value': '/dashboard/ai-value',
  coordinators: '/dashboard/by/coordinator',
  vendors: '/dashboard/by/vendor',
  properties: '/dashboard/by/building',
  'issue-types': '/dashboard/by/trade',
  inspections: '/dashboard/by/category',
  'items-changed': '/dashboard/items-changed',
};

/**
 * `/analytics-v2?…` → the Dashboard, or the sub-page that took the old `tab`
 * over (an old bookmark or an email link still lands on the right table);
 * `propertyId` survives, and `metric` only for the AI value page (its drill).
 * `tab=costs` has no page — its line is the Dashboard's "Spend, 12 months".
 */
export function analyticsV2Redirect(search: string): string {
  const params = new URLSearchParams(search);
  const propertyId = params.get('propertyId');
  const tab = params.get('tab');
  const path = (tab && TAB_TO_PATH[tab]) || '/dashboard';
  const next = new URLSearchParams();
  if (propertyId) next.set('propertyId', propertyId);
  const metric = params.get('metric');
  if (tab === 'ai-value' && metric) next.set('metric', metric);
  const qs = next.toString();
  return qs ? `${path}?${qs}` : path;
}
